import React, { useEffect, useMemo } from "react";
import { Outlet, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import "./AppLayout.scss";
import AppFooter from "../../AppFooter/AppFooter";
import { usePwaContext } from "../../../context/pwa-context";
import { checkOs, openApp, removeTrailingSlash } from "../../../utils/helpers";
import {
  getStoredHasShownNotificationPrompt,
  getStoredPwaInstallOrigin,
  setStoredHasShownNotificationPrompt,
} from "../../../utils/storage";
import { ROUTES } from "../../../utils/constants";
import GameControllerIcon from "../../icons/GameControllerIcon";
import TrophyIcon from "../../icons/TrophyIcon";
import NotificationFlow from "../../notification-flow/NotificationFlow";
import useNotifications from "../../../hooks/useNotifications";
import useAuth from "../../../hooks/useAuth";
import useDeepLink from "../../../hooks/useDeepLink";
import LoadingOverlay, {
  OverlayVariant,
} from "../../LoadingOverlay/LoadingOverlay";
import DownloadAppPrompt, {
  PROMPT_DOWNLOAD_SCENARIOS,
} from "../../DownloadAppPrompt/DownloadAppPrompt";
import useDisclosure from "../../../hooks/useDisclosure";
import useUserProfileQuery from "../../../react-query/queries/use-user-profile";
import { renderHeaders } from "../../../utils/renderHeaders";
import { OperatingSystems } from "../../../utils/vendor";
import ScrollToTop from "../../ScrollToTop";
import SigmaGamesLogoIcon from "../../icons/SigmaGamesLogoIcon";
import { AppNavigationRoute } from "./types";
import GameDetailsDrawer from "../../modals/GameDetailsDrawer/GameDetailsDrawer";
import PayWallDrawer from "../../PayWall/PayWallDrawer";
import { useRevenueCatInit } from "../../../hooks/useRevenueCatInit";
import useUtmAttribution from "../../../hooks/useUtmAttribution";
import { useUnviewedResultCountQuery } from "../../../react-query/queries/use-result-activities";

const useAppNavigationRoutes = (): AppNavigationRoute[] => {
  const { t } = useTranslation("common");
  const { data: unviewedCount } = useUnviewedResultCountQuery();
  const count = unviewedCount?.unviewedCount ?? 0;
  const badge = count > 0 ? count : undefined;

  return useMemo(
    () => [
      {
        name: t("home"),
        icon: SigmaGamesLogoIcon,
        href: ROUTES.ROOT.HOME,
        isProtected: true,
        requiresApp: false,
      },
      {
        name: t("games"),
        icon: GameControllerIcon,
        href: ROUTES.GAMES.ROOT,
        isProtected: false,
        requiresApp: false,
      },
      {
        name: t("results"),
        icon: TrophyIcon,
        href: ROUTES.ROOT.RESULTS,
        isProtected: true,
        requiresApp: false,
        badge,
      },
    ],
    [t, badge]
  );
};

const HOMEPAGE = ROUTES.ROOT.HOME;
const GAMES_PAGE = ROUTES.GAMES.ROOT;
const SEO_GENRES_PAGE = ROUTES.GAMES.GENRES_SEO;

function AppLayout() {
  useUserProfileQuery();
  useRevenueCatInit();
  useUtmAttribution();
  const { slug, genreName, subGenre } = useParams();
  const { pathname } = useLocation();
  const { t } = useTranslation("common");
  const { isLoggedIn: userIsLoggedIn } = useAuth();
  const { isTryingToOpenMobileApp } = useDeepLink();
  const appNavigationRoutes = useAppNavigationRoutes();
  const os = checkOs();

  const isGamesPageRoute =
    removeTrailingSlash(pathname) === GAMES_PAGE ||
    removeTrailingSlash(pathname) === `${GAMES_PAGE}/${slug}`;

  const isSeoGenrePageRoute =
    removeTrailingSlash(pathname) === `${SEO_GENRES_PAGE}/${genreName}` ||
    removeTrailingSlash(pathname) ===
      `${SEO_GENRES_PAGE}/${genreName}/${subGenre}`;

  const {
    notificationsAreEnabled,
    showPeriodicNotificationPrompt,
    ...notifications
  } = useNotifications();

  const { userIsInPwa, appInstallHasCompleted, resetInstallationParams } =
    usePwaContext();

  const {
    isOpen: isDownloadAppPromptOpen,
    onOpen: openDownloadAppPrompt,
    onClose: closeDownloadAppPrompt,
  } = useDisclosure();
  const pathIsANavRoute = useMemo(
    () =>
      pathname === HOMEPAGE ||
      removeTrailingSlash(pathname) === `${GAMES_PAGE}/${slug}` ||
      !!appNavigationRoutes.find(
        (route) => removeTrailingSlash(pathname) === route.href
      ),
    [appNavigationRoutes, pathname, slug]
  );

  const showContentPage = isSeoGenrePageRoute && !userIsLoggedIn;
  const canShowPwaNavAndBanner = pathIsANavRoute && !showContentPage;

  // Trigger the prompt to turn on notifications automatically once per session and once within 24 hours across different sessions.
  useEffect(() => {
    const hasShownNotificationPrompt = getStoredHasShownNotificationPrompt();
    if (
      !userIsLoggedIn ||
      !pathIsANavRoute ||
      notificationsAreEnabled ||
      hasShownNotificationPrompt
    )
      return;

    showPeriodicNotificationPrompt(24);
    setStoredHasShownNotificationPrompt(true);
  }, [
    userIsLoggedIn,
    pathIsANavRoute,
    notificationsAreEnabled,
    showPeriodicNotificationPrompt,
  ]);

  // Open the PWA 3 seconds after installation has completed.
  useEffect(() => {
    const pwaInstallOrigin = getStoredPwaInstallOrigin();
    // eslint-disable-next-line no-undef
    let openAppTimer: NodeJS.Timeout;
    if (appInstallHasCompleted && !userIsInPwa) {
      openAppTimer = setTimeout(() => {
        resetInstallationParams();
        openApp(pwaInstallOrigin || ROUTES.GAMES.ROOT);
      }, 3000);
    }

    return () => {
      clearTimeout(openAppTimer);
    };
  }, [appInstallHasCompleted, userIsInPwa, resetInstallationParams]);

  if (isTryingToOpenMobileApp && pathIsANavRoute) {
    return (
      <div className="LoaderWrapper MobileOnly">
        <LoadingOverlay variant={OverlayVariant.BLOCKS} />
      </div>
    );
  }

  return (
    <>
      <ScrollToTop />
      {renderHeaders({
        showAppBanner: pathIsANavRoute,
        title: t("metadata.battle_friends_in_fun_games_and_make_money"),
        description: t(
          "metadata.battle_friends_and_frenemies_in_fun_games_to_win_the_prize_pool"
        ),
        ogDescription: t(
          "metadata.battle_friends_and_frenemies_in_fun_casual_games_to_win_$1k"
        ),
        ogTitle:
          "Battle Friends in Fun Games & Make Money | Hyper by Metaverse Magna",
      })}

      <div
        className={`AppLayout ${!showContentPage ? "MobileOnly" : ""} ${
          os === OperatingSystems.Ios ? "Ios" : ""
        } ${userIsInPwa ? "Pwa" : ""}`}
      >
        <div
          className={`position-relative MainContent ${
            isGamesPageRoute ? "GamesPageContent" : ""
          } ${canShowPwaNavAndBanner ? "NavIsShowing" : ""}`}
        >
          <Outlet />
          {canShowPwaNavAndBanner && <AppFooter routes={appNavigationRoutes} />}
        </div>
        <NotificationFlow
          {...notifications}
          handleGetApp={openDownloadAppPrompt}
        />
        <DownloadAppPrompt
          isOpen={isDownloadAppPromptOpen}
          onOpen={openDownloadAppPrompt}
          onClose={closeDownloadAppPrompt}
          showNotificationsFlow={false}
          promptDownloadScenario={PROMPT_DOWNLOAD_SCENARIOS.LOGGED_IN.OTHERS}
        />
        <GameDetailsDrawer />
        <PayWallDrawer />
      </div>
    </>
  );
}

export default AppLayout;
